import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { DbSiteSetting } from '@/types';
import { dbToSiteSetting } from '@/types';

interface CountdownState {
  countdownActive: boolean;
  targetDate: string | null;
  registrationBlocked: boolean;
  isLoading: boolean;
}

/**
 * Hook to manage countdown timer state from site settings
 * Fetches countdown settings and determines if registration should be blocked
 */
export function useCountdown(): CountdownState {
  const [state, setState] = useState<CountdownState>({
    countdownActive: false,
    targetDate: null,
    registrationBlocked: false,
    isLoading: true,
  });

  useEffect(() => {
    const fetchCountdownSettings = async () => {
      try {
        // Fetch site settings
        const { data: settings, error } = await supabase
          .from('site_settings')
          .select('*')
          .in('setting_key', ['countdown_enabled', 'countdown_target_date'])
          .returns<DbSiteSetting[]>();

        if (error) throw error;

        if (import.meta.env.DEV) console.log('[useCountdown] Fetched settings:', settings);

        // Convert database settings to domain format
        const domainSettings = settings?.map(dbToSiteSetting) || [];

        // Parse settings
        const enabled = domainSettings.find(
          s => s.settingKey === 'countdown_enabled'
        )?.settingValue === 'true';

        const target = domainSettings.find(
          s => s.settingKey === 'countdown_target_date'
        )?.settingValue;

        if (import.meta.env.DEV) console.log('[useCountdown] Parsed values:', { enabled, target });

        if (enabled && target && target.trim() !== '') {
          const now = new Date();
          const targetTime = new Date(target);
          const isBlocked = now < targetTime;

          if (import.meta.env.DEV) console.log('[useCountdown] Countdown state:', {
            enabled,
            target,
            now: now.toISOString(),
            targetTime: targetTime.toISOString(),
            isBlocked
          });

          setState({
            countdownActive: isBlocked,
            targetDate: target,
            registrationBlocked: isBlocked,
            isLoading: false,
          });
        } else {
          if (import.meta.env.DEV) console.log('[useCountdown] Countdown disabled or no target date');
          setState({
            countdownActive: false,
            targetDate: null,
            registrationBlocked: false,
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Error fetching countdown settings:', error);
        setState({
          countdownActive: false,
          targetDate: null,
          registrationBlocked: false,
          isLoading: false,
        });
      }
    };

    fetchCountdownSettings();

    // Refresh settings every minute in case they change
    const interval = setInterval(fetchCountdownSettings, 60000);

    return () => clearInterval(interval);
  }, []);

  // Update countdown status when time passes
  useEffect(() => {
    if (!state.targetDate || !state.countdownActive) return;

    const checkCountdownStatus = () => {
      const now = new Date();
      const targetTime = new Date(state.targetDate!);

      if (now >= targetTime) {
        setState(prev => ({
          ...prev,
          countdownActive: false,
          registrationBlocked: false,
        }));
      }
    };

    // Check every second
    const interval = setInterval(checkCountdownStatus, 1000);

    return () => clearInterval(interval);
  }, [state.targetDate, state.countdownActive]);

  return state;
}
